import React from 'react'
import styled from '@emotion/styled'
import facepaint from 'facepaint'

import { COLORS } from '@borrowell/bw-styles'
import mq from '../styles/breakpoints'
import { FONT_FAMILY } from '../styles/theme'

// Types and interfaces
import { Block, Inline, Text } from '@contentful/rich-text-types'

/**
 * `@contentful/rich-text-types@14` predates Contentful's rich-text tables, so `BLOCKS` has no
 * table members. The renderer looks `renderNode` handlers up by the raw `nodeType` string, so
 * these literals are all that is needed — no package bump.
 * See https://www.contentful.com/developers/changelog/create-tables-in-the-rich-text-editor/
 */
export const TABLE_NODE_TYPES = {
  TABLE: 'table',
  TABLE_ROW: 'table-row',
  TABLE_CELL: 'table-cell',
  TABLE_HEADER_CELL: 'table-header-cell',
} as const

const BORDER = `1px solid ${COLORS.NEUTRAL.COOL['300']}`

/**
 * Rich-text tables are authored freely, so they can be wider than the viewport. The wrapper keeps
 * a wide table scrollable instead of forcing the page into a horizontal scroll.
 */
const TableScrollContainer = styled.div<{ styleOverrides?: facepaint.BaseArg }>(({ styleOverrides }) =>
  mq({
    width: '100%',
    overflowX: 'auto',
    margin: '0 0 25px 0',
    ...styleOverrides,
  }),
)

const StyledTable = styled.table<{ styleOverrides?: facepaint.BaseArg }>(({ styleOverrides }) =>
  mq({
    width: '100%',
    border: BORDER,
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderRadius: '8px',
    fontFamily: FONT_FAMILY.LATO,
    tableLayout: 'auto',
    // Corner rounding and the trailing border live on the table so that the rules key off the
    // real first/last row. On a row they would re-fire for the first `<tr>` of every section.
    // With no `<thead>` the body is the first child, so one pair of rules covers both shapes.
    '& > thead > tr:first-of-type > th:first-of-type, & > tbody:first-of-type > tr:first-of-type > :first-of-type': {
      borderTopLeftRadius: '8px',
    },
    '& > thead > tr:first-of-type > th:last-of-type, & > tbody:first-of-type > tr:first-of-type > :last-of-type': {
      borderTopRightRadius: '8px',
    },
    '& > tbody > tr:last-of-type > *': {
      borderBottom: 'none',
    },
    '& > tbody > tr:last-of-type > :first-of-type': {
      borderBottomLeftRadius: '8px',
    },
    '& > tbody > tr:last-of-type > :last-of-type': {
      borderBottomRightRadius: '8px',
    },
    ...styleOverrides,
  }),
)

const CELL_PADDING = ['13px', '20px', '20px']

const commonCellStyles = {
  textAlign: 'left',
  verticalAlign: 'top',
  fontFamily: FONT_FAMILY.LATO,
  fontWeight: '400',
  fontSize: '18px',
  padding: CELL_PADDING,
  borderBottom: BORDER,
  lineHeight: 1.5,
  // `reset.css` zeroes the outer padding via `th:first-child, td:first-child { padding-left: 0 }`
  // (and the `:last-child` mirror). Those beat a plain class, so the edge cells need a pseudo-class
  // of their own to win the cascade — the same workaround `GenericTableSection` uses.
  ':first-of-type': {
    paddingLeft: CELL_PADDING,
  },
  ':last-of-type': {
    paddingRight: CELL_PADDING,
  },
  // Cell content arrives as paragraph nodes carrying centred, width-capped body-copy styles.
  // Hand typography back to the cell so a table reads as a table, not as stacked paragraphs.
  '& > p': {
    margin: 0,
    maxWidth: 'none',
    textAlign: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
  },
  '& > p + p': {
    marginTop: '12px',
  },
}

const StyledTh = styled.th<{ styleOverrides?: facepaint.BaseArg }>(({ styleOverrides }) =>
  mq({
    ...commonCellStyles,
    fontWeight: '700',
    fontSize: '19px',
    backgroundColor: COLORS.NEUTRAL.COOL['100'],
    ...styleOverrides,
  }),
)

const StyledTd = styled.td<{ styleOverrides?: facepaint.BaseArg }>(({ styleOverrides }) =>
  mq({
    ...commonCellStyles,
    ...styleOverrides,
  }),
)

const StyledTr = styled.tr<{ styleOverrides?: facepaint.BaseArg }>(({ styleOverrides }) => mq({ ...styleOverrides }))

/**
 * A header cell is a column header when every cell in its row is a header cell, and a row header
 * otherwise (Contentful's "header column" option). Only the row knows which, so it tells the cell.
 */
const HeaderRowContext = React.createContext(false)

const isHeaderRow = (node: Block | Inline | Text) =>
  'content' in node &&
  node.content.length > 0 &&
  // `nodeType` is widened to string because the v14 enums have no table members.
  node.content.every(cell => (cell.nodeType as string) === TABLE_NODE_TYPES.TABLE_HEADER_CELL)

interface ITableCellNode extends Block {
  data: {
    colspan?: number
    rowspan?: number
  }
}

const cellSpans = (node: Block | Inline) => {
  const { colspan, rowspan } = (node as ITableCellNode).data || {}
  return {
    colSpan: colspan && colspan > 1 ? colspan : undefined,
    rowSpan: rowspan && rowspan > 1 ? rowspan : undefined,
  }
}

/**
 * Contentful lets authors mark the first row, the first column, or both as headers. Only a fully
 * header-cell first row becomes a `<thead>`; a header column stays in the body as `<th>` cells.
 */
const leadingHeaderRowCount = (node: Block | Inline) => {
  if (!('content' in node)) return 0

  let count = 0
  for (const row of node.content) {
    if (!isHeaderRow(row)) break
    count += 1
  }

  // An all-header table is a table of data, not a table of headers.
  return count === node.content.length ? 0 : count
}

interface ITableProps {
  node: Block | Inline
  styleOverrides?: facepaint.BaseArg
  wrapperStyleOverrides?: facepaint.BaseArg
}

export const RichTextTable: React.FC<ITableProps> = ({ node, styleOverrides, wrapperStyleOverrides, children }) => {
  const rows = React.Children.toArray(children)
  const headerRowCount = leadingHeaderRowCount(node)
  // Only split when the rendered rows line up with the document, so an unexpected shape still renders.
  const canSplit = headerRowCount > 0 && 'content' in node && rows.length === node.content.length

  return (
    <TableScrollContainer styleOverrides={wrapperStyleOverrides}>
      <StyledTable styleOverrides={styleOverrides}>
        {canSplit && <thead>{rows.slice(0, headerRowCount)}</thead>}
        <tbody>{canSplit ? rows.slice(headerRowCount) : rows}</tbody>
      </StyledTable>
    </TableScrollContainer>
  )
}

interface ICellProps {
  node: Block | Inline
  styleOverrides?: facepaint.BaseArg
}

export const RichTextTableRow: React.FC<ICellProps> = ({ node, styleOverrides, children }) => (
  <HeaderRowContext.Provider value={isHeaderRow(node)}>
    <StyledTr styleOverrides={styleOverrides}>{children}</StyledTr>
  </HeaderRowContext.Provider>
)

export const RichTextTableCell: React.FC<ICellProps> = ({ node, styleOverrides, children }) => (
  <StyledTd styleOverrides={styleOverrides} {...cellSpans(node)}>
    {children}
  </StyledTd>
)

export const RichTextTableHeaderCell: React.FC<ICellProps> = ({ node, styleOverrides, children }) => {
  const scope = React.useContext(HeaderRowContext) ? 'col' : 'row'

  return (
    <StyledTh scope={scope} styleOverrides={styleOverrides} {...cellSpans(node)}>
      {children}
    </StyledTh>
  )
}
